import { useState, useCallback, useRef } from "react";

type CaptchaProvider = "recaptcha" | "turnstile";
type CaptchaVersion = "v2" | "v3";

declare global {
  interface Window {
    grecaptcha: any;
    turnstile?: {
      render: (
        container: string | HTMLElement,
        options: Record<string, unknown>,
      ) => string;
      execute?: (widgetId: string) => void;
      reset: (widgetId?: string) => void;
      remove?: (widgetId: string) => void;
    };
  }
}

export interface UseCaptchaReturn {
  executeV3: (action: string) => Promise<string>;
  executeV2: () => Promise<string>;
  reset: () => void;
  ready: boolean;
  error: string | null;
  v2Configured: boolean;
  v3Configured: boolean;
  provider: CaptchaProvider;
  lastUsedProvider: CaptchaProvider | null;
}

export interface CaptchaRuntimeConfig {
  preferred_provider?: CaptchaProvider;
  available_providers?: CaptchaProvider[];
  bypassed?: boolean;
  turnstile_site_key?: string | null;
  recaptcha_v2_site_key?: string | null;
  recaptcha_v3_site_key?: string | null;
}

interface UseCaptchaOptions {
  containerId?: string;
  runtimeConfig?: CaptchaRuntimeConfig | null;
}

function isCaptchaBypassed(runtimeConfig?: CaptchaRuntimeConfig | null): boolean {
  if (runtimeConfig && typeof runtimeConfig.bypassed === "boolean") {
    return runtimeConfig.bypassed;
  }

  return String(import.meta.env.VITE_SKIP_CAPTCHA || "")
    .trim()
    .toLowerCase() === "true";
}

function getCaptchaProvider(
  runtimeConfig?: CaptchaRuntimeConfig | null,
): CaptchaProvider {
  const rawProvider = runtimeConfig
    ? runtimeConfig.preferred_provider
    : import.meta.env.VITE_CAPTCHA_PROVIDER || "turnstile";

  return String(rawProvider || "turnstile")
    .trim()
    .toLowerCase() === "recaptcha"
    ? "recaptcha"
    : "turnstile";
}

function isPlaceholderSiteKey(siteKey: string): boolean {
  const normalized = siteKey.trim().toLowerCase();
  return (
    normalized.length === 0 ||
    normalized === "your-v3-site-key" ||
    normalized === "your-v2-site-key" ||
    normalized === "your-turnstile-site-key" ||
    normalized === "your_recaptcha_v3_site_key_here" ||
    normalized === "your_recaptcha_v2_site_key_here" ||
    normalized === "your_turnstile_site_key_here" ||
    /^your[-_].*site[-_]?key/.test(normalized)
  );
}

function getSiteKey(
  version: "v2" | "v3" | "turnstile",
  runtimeConfig?: CaptchaRuntimeConfig | null,
): string | null {
  if (isCaptchaBypassed(runtimeConfig)) {
    return null;
  }

  const rawSiteKey = runtimeConfig
    ? version === "turnstile"
      ? runtimeConfig.turnstile_site_key
      : version === "v3"
        ? runtimeConfig.recaptcha_v3_site_key
        : runtimeConfig.recaptcha_v2_site_key
    : version === "turnstile"
      ? import.meta.env.VITE_TURNSTILE_SITE_KEY
      : version === "v3"
        ? import.meta.env.VITE_RECAPTCHA_V3_SITE_KEY
        : import.meta.env.VITE_RECAPTCHA_V2_SITE_KEY;

  if (typeof rawSiteKey !== "string") {
    return null;
  }

  const siteKey = rawSiteKey.trim();
  return isPlaceholderSiteKey(siteKey) ? null : siteKey;
}

/**
 * CAPTCHA integration hook.
 * Supports Google reCAPTCHA and Cloudflare Turnstile.
 */
export const useCaptcha = (
  options: UseCaptchaOptions = {},
): UseCaptchaReturn => {
  const [ready, setReady] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastUsedProvider, setLastUsedProvider] = useState<CaptchaProvider | null>(null);
  const runtimeConfig = options.runtimeConfig || null;
  const preferredProvider = getCaptchaProvider(runtimeConfig);
  const captchaContainerId = options.containerId || "captcha-container";
  const internalTurnstileContainerRef = useRef<HTMLDivElement | null>(null);
  const turnstileWidgetIdRef = useRef<string | null>(null);
  const recaptchaWidgetIdRef = useRef<number | null>(null);
  const turnstileSiteKey = getSiteKey("turnstile", runtimeConfig);
  const recaptchaV2SiteKey = getSiteKey("v2", runtimeConfig);
  const recaptchaV3SiteKey = getSiteKey("v3", runtimeConfig);
  const turnstileConfigured = turnstileSiteKey !== null;
  const recaptchaV2Configured = recaptchaV2SiteKey !== null;
  const recaptchaV3Configured = recaptchaV3SiteKey !== null;

  const isProviderConfigured = useCallback(
    (provider: CaptchaProvider, version: CaptchaVersion): boolean => {
      if (provider === "turnstile") {
        return turnstileConfigured;
      }

      return version === "v3" ? recaptchaV3Configured : recaptchaV2Configured;
    },
    [recaptchaV2Configured, recaptchaV3Configured, turnstileConfigured],
  );

  const getProviderCandidates = useCallback(
    (version: CaptchaVersion): CaptchaProvider[] => {
      const orderedProviders: CaptchaProvider[] =
        preferredProvider === "turnstile"
          ? ["turnstile", "recaptcha"]
          : ["recaptcha", "turnstile"];

      return orderedProviders.filter((provider) =>
        isProviderConfigured(provider, version),
      );
    },
    [isProviderConfigured, preferredProvider],
  );

  const provider =
    getProviderCandidates("v3")[0] ||
    getProviderCandidates("v2")[0] ||
    preferredProvider;
  const v2Configured = getProviderCandidates("v2").length > 0;
  const v3Configured = getProviderCandidates("v3").length > 0;

  // Load provider runtime script.
  const loadScript = useCallback(
    (provider: CaptchaProvider, version: CaptchaVersion) => {
      return new Promise<void>((resolve, reject) => {
      if (provider === "turnstile") {
        if (window.turnstile) {
          setReady(true);
          return resolve();
        }

        const siteKey = turnstileSiteKey;
        if (!siteKey) {
          const err = "Turnstile site key not configured";
          setError(err);
          return reject(new Error(err));
        }

        const existingScript = document.querySelector<HTMLScriptElement>(
          'script[data-captcha-provider="turnstile"]',
        );
        const handleLoad = () => {
          setReady(true);
          resolve();
        };
        const handleError = () => {
          const err = "Failed to load Turnstile script";
          setError(err);
          reject(new Error(err));
        };

        if (existingScript) {
          if (window.turnstile) {
            setReady(true);
            resolve();
            return;
          }

          existingScript.addEventListener("load", handleLoad, { once: true });
          existingScript.addEventListener("error", handleError, {
            once: true,
          });
          return;
        }

        const script = document.createElement("script");
        script.src =
          "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
        script.async = true;
        script.defer = true;
        script.dataset.captchaProvider = "turnstile";
        script.onload = handleLoad;
        script.onerror = handleError;
        document.head.appendChild(script);
        return;
      }

      // Check if already loaded
      if (window.grecaptcha) {
        setReady(true);
        return resolve();
      }

      const siteKey = version === "v3" ? recaptchaV3SiteKey : recaptchaV2SiteKey;

      if (!siteKey) {
        const err = `reCAPTCHA ${version} site key not configured`;
        setError(err);
        return reject(new Error(err));
      }

      const script = document.createElement("script");
      script.src =
        version === "v3"
          ? `https://www.google.com/recaptcha/api.js?render=${siteKey}`
          : "https://www.google.com/recaptcha/api.js";
      script.async = true;
      script.defer = true;

      script.onload = () => {
        window.grecaptcha.ready(() => {
          setReady(true);
          resolve();
        });
      };

      script.onerror = () => {
        const err = "Failed to load reCAPTCHA script";
        setError(err);
        reject(new Error(err));
      };

      document.head.appendChild(script);
    });
    },
    [recaptchaV2SiteKey, recaptchaV3SiteKey, turnstileSiteKey],
  );

  const getOrCreateTurnstileContainer = useCallback(async (): Promise<HTMLElement> => {
    const maxAttempts = 5;
    for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
      const existingContainer = document.getElementById(captchaContainerId);
      if (existingContainer) {
        return existingContainer;
      }
      await new Promise<void>((resolve) => {
        window.requestAnimationFrame(() => resolve());
      });
    }

    if (!internalTurnstileContainerRef.current) {
      const fallbackContainer = document.createElement("div");
      fallbackContainer.setAttribute("data-captcha-fallback", "turnstile");
      fallbackContainer.style.position = "fixed";
      fallbackContainer.style.width = "1px";
      fallbackContainer.style.height = "1px";
      fallbackContainer.style.opacity = "0";
      fallbackContainer.style.pointerEvents = "none";
      fallbackContainer.style.left = "-9999px";
      fallbackContainer.style.top = "-9999px";
      document.body.appendChild(fallbackContainer);
      internalTurnstileContainerRef.current = fallbackContainer;
    }

    return internalTurnstileContainerRef.current;
  }, [captchaContainerId]);

  const cleanupInternalTurnstileContainer = useCallback(() => {
    if (internalTurnstileContainerRef.current) {
      internalTurnstileContainerRef.current.remove();
      internalTurnstileContainerRef.current = null;
    }
  }, []);

  const executeTurnstile = useCallback(
    async (action?: string): Promise<string> => {
      await loadScript("turnstile", "v3");

      const siteKey = turnstileSiteKey;
      if (!siteKey) {
        throw new Error("Turnstile site key not configured");
      }

      const container = await getOrCreateTurnstileContainer();

      if (!window.turnstile) {
        throw new Error("Turnstile runtime unavailable");
      }

      if (
        turnstileWidgetIdRef.current &&
        typeof window.turnstile.remove === "function"
      ) {
        window.turnstile.remove(turnstileWidgetIdRef.current);
      }

      container.innerHTML = "";

      return new Promise<string>((resolve, reject) => {
        const widgetId = window.turnstile!.render(container, {
          sitekey: siteKey,
          action,
          execution: "execute",
          appearance: "interaction-only",
          callback: (token: string) => {
            setReady(true);
            resolve(token);
          },
          "error-callback": () => {
            const err = "Turnstile verification failed";
            setError(err);
            reject(new Error(err));
          },
          "expired-callback": () => {
            const err = "Turnstile challenge expired";
            setError(err);
            reject(new Error(err));
          },
        });

        turnstileWidgetIdRef.current = widgetId;
        if (typeof window.turnstile?.execute === "function") {
          window.turnstile.execute(widgetId);
        }
      });
    },
    [getOrCreateTurnstileContainer, loadScript, turnstileSiteKey],
  );

  const executeRecaptchaV3 = useCallback(
    async (action: string): Promise<string> => {
      await loadScript("recaptcha", "v3");

      const siteKey = recaptchaV3SiteKey;
      if (!siteKey) {
        throw new Error("reCAPTCHA v3 site key not configured");
      }

      return window.grecaptcha.execute(siteKey, { action });
    },
    [loadScript, recaptchaV3SiteKey],
  );

  const executeRecaptchaV2 = useCallback(async (): Promise<string> => {
    await loadScript("recaptcha", "v2");

    return new Promise<string>((resolve, reject) => {
      const siteKey = recaptchaV2SiteKey;
      if (!siteKey) {
        const err = "reCAPTCHA v2 site key not configured";
        setError(err);
        reject(new Error(err));
        return;
      }

      const widgetId = window.grecaptcha.render(captchaContainerId, {
        sitekey: siteKey,
        callback: (token: string) => {
          resolve(token);
        },
        "error-callback": () => {
          const err = "CAPTCHA v2 verification failed";
          setError(err);
          reject(new Error(err));
        },
        "expired-callback": () => {
          const err = "CAPTCHA expired";
          setError(err);
          reject(new Error(err));
        },
      });

      recaptchaWidgetIdRef.current = widgetId;
    });
  }, [captchaContainerId, loadScript, recaptchaV2SiteKey]);

  const executeWithFallback = useCallback(
    async (version: CaptchaVersion, action?: string): Promise<string> => {
      const candidates = getProviderCandidates(version);
      if (candidates.length === 0) {
        throw new Error(`No CAPTCHA provider configured for ${version}`);
      }

      let lastError: Error | null = null;
      for (let i = 0; i < candidates.length; i += 1) {
        const candidate = candidates[i];
        try {
          const token =
            candidate === "turnstile"
              ? await executeTurnstile(action)
              : version === "v3"
                ? await executeRecaptchaV3(action || "submit")
                : await executeRecaptchaV2();
          setLastUsedProvider(candidate);
          return token;
        } catch (err) {
          lastError =
            err instanceof Error
              ? err
              : new Error(`CAPTCHA ${version} execution failed`);

          if (i < candidates.length - 1) {
            setError(
              `Primary CAPTCHA provider unavailable. Falling back to ${candidates[i + 1]}.`,
            );
          }
        }
      }

      throw lastError || new Error(`CAPTCHA ${version} execution failed`);
    },
    [executeRecaptchaV2, executeRecaptchaV3, executeTurnstile, getProviderCandidates],
  );

  /**
   * Execute reCAPTCHA v3 (invisible) and get token
   */
  const executeV3 = useCallback(
    async (action: string): Promise<string> => {
      try {
        return await executeWithFallback("v3", action);
      } catch (err) {
        const message =
          err instanceof Error ? err.message : "CAPTCHA v3 execution failed";
        setError(message);
        throw new Error(message);
      }
    },
    [executeWithFallback],
  );

  /**
   * Execute reCAPTCHA v2 (checkbox) and get token
   */
  const executeV2 = useCallback(async (): Promise<string> => {
    try {
      return await executeWithFallback("v2");
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "CAPTCHA v2 execution failed";
      setError(message);
      throw new Error(message);
    }
  }, [executeWithFallback]);

  /**
   * Reset CAPTCHA (useful for retry scenarios)
   */
  const reset = useCallback(() => {
    if (window.grecaptcha && recaptchaWidgetIdRef.current !== null) {
      window.grecaptcha.reset(recaptchaWidgetIdRef.current);
    }

    if (provider === "turnstile" && turnstileWidgetIdRef.current) {
      if (typeof window.turnstile?.remove === "function") {
        window.turnstile.remove(turnstileWidgetIdRef.current);
      } else {
        window.turnstile?.reset(turnstileWidgetIdRef.current);
      }
      turnstileWidgetIdRef.current = null;
      const container = document.getElementById(captchaContainerId);
      if (container) {
        container.innerHTML = "";
      }
      cleanupInternalTurnstileContainer();
    }

    setLastUsedProvider(null);
    setError(null);
  }, [captchaContainerId, cleanupInternalTurnstileContainer, provider]);

  return {
    executeV3,
    executeV2,
    reset,
    ready,
    error,
    v2Configured,
    v3Configured,
    provider,
    lastUsedProvider,
  };
};
